/* Live data hooks backed by the B2Brouter proxy.
   No demo fallback: real users see real data or an empty/error state.
   `source` reflects the request lifecycle: 'loading' → 'live' | 'error'. */
import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import type { Invoice, Received, Client, Supplier } from './demo';

export type Source = 'live' | 'loading' | 'error';

type Resource<T> = { rows: T[]; source: Source; reload: () => void };

function useResource<T>(path: string, pick: (json: unknown) => T[]): Resource<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [source, setSource] = useState<Source>('loading');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setSource('loading');
    apiGet<unknown>(path, ctrl.signal)
      .then((json) => {
        if (ctrl.signal.aborted) return;
        setRows(pick(json));
        setSource('live');
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setRows([]);
        setSource('error');
        console.warn(`[factumis] live fetch ${path} failed`, err);
      });
    return () => ctrl.abort();
  }, [path, tick]);

  return { rows, source, reload: () => setTick((t) => t + 1) };
}

export function useInvoices(): Resource<Invoice> {
  return useResource<Invoice>('/api/b2b/invoices', (j) => (j as { invoices?: Invoice[] }).invoices ?? []);
}

export function useReceived(): Resource<Received> {
  return useResource<Received>('/api/b2b/received', (j) => (j as { received?: Received[] }).received ?? []);
}

export function useClients(): Resource<Client> {
  return useResource<Client>('/api/b2b/clients', (j) => (j as { clients?: Client[] }).clients ?? []);
}

// ── Fiche client (contact B2Brouter, CRUD) ────────────────────────────────────
export type ClientDetail = {
  id: number;
  name: string;
  siren: string;
  countryCode: string;
  vatId: string;
  email: string;
  address: string;
  postalcode: string;
  city: string;
  province: string;
  // Au moins une facture déjà rattachée (tous statuts, brouillon compris) —
  // renseigné uniquement par GET /b2b/clients/:id (absent de create/update).
  hasInvoices?: boolean;
};

export type ClientInput = {
  name: string;
  siren?: string;
  countryCode?: string;
  vatId?: string;
  email?: string;
  address?: string;
  postalcode?: string;
  city?: string;
  province?: string;
};

export function getClientDetail(id: number, signal?: AbortSignal): Promise<ClientDetail> {
  return apiGet<{ client: ClientDetail }>(`/api/b2b/clients/${id}`, signal).then((d) => d.client);
}

export function createClient(input: ClientInput): Promise<ClientDetail> {
  return apiPost<{ client: ClientDetail }>('/api/b2b/clients', input).then((d) => d.client);
}

export function updateClient(id: number, input: ClientInput): Promise<ClientDetail> {
  return apiPatch<{ client: ClientDetail }>(`/api/b2b/clients/${id}`, input).then((d) => d.client);
}

// Lignes au format API (HT en euros) — partagé par avoir/rectificative/solde.
export type DocLine = { description: string; quantity: number; price: number; vat: number };

/** Avoir (kind 'credit_note') ou rectificative (kind 'corrective') liés à une facture. */
export function amendInvoice(invoiceId: string, body: { kind: 'credit_note' | 'corrective'; reason?: string; lines: DocLine[] }): Promise<Invoice> {
  return apiPost<{ invoice: Invoice }>(`/api/b2b/invoices/${invoiceId}/amend`, body).then((d) => d.invoice);
}

/** Facture de solde déduisant l'acompte `prepaymentInvoiceId` ; lines = facture complète. */
export function settleInvoice(prepaymentInvoiceId: string, body: { lines: DocLine[]; dueDays?: number }): Promise<Invoice> {
  return apiPost<{ invoice: Invoice }>(`/api/b2b/invoices/${prepaymentInvoiceId}/settle`, body).then((d) => d.invoice);
}

// ── Factures récurrentes ───────────────────────────────────────────────────────
export type Cadence = 'monthly' | 'quarterly' | 'yearly';
export interface RecurringInvoice {
  id: number;
  clientName: string;
  clientSiren: string | null;
  clientEmail: string | null;
  lines: DocLine[];
  cadence: Cadence;
  nextRun: string; // 'YYYY-MM-DD'
  active: boolean;
}

export function useRecurring(): Resource<RecurringInvoice> {
  return useResource<RecurringInvoice>('/api/recurring', (j) => (j as { recurring?: RecurringInvoice[] }).recurring ?? []);
}

export function createRecurring(input: {
  clientName: string; clientSiren?: string; clientEmail?: string;
  lines: DocLine[]; cadence: Cadence; firstRun: string;
}): Promise<RecurringInvoice> {
  return apiPost<{ recurring: RecurringInvoice }>('/api/recurring', input).then((d) => d.recurring);
}

export function toggleRecurring(id: number, active: boolean): Promise<RecurringInvoice> {
  return apiPost<{ recurring: RecurringInvoice }>(`/api/recurring/${id}/toggle`, { active }).then((d) => d.recurring);
}

export function useSuppliers(): Resource<Supplier> {
  return useResource<Supplier>('/api/b2b/suppliers', (j) => (j as { suppliers?: Supplier[] }).suppliers ?? []);
}

// E-reporting B2C (déclarations DGFiP des ventes aux particuliers), Task 6/7.
// Saisie et suppression restent possibles en mode dégradé (org non raccordée à
// Seqino) ; seule la transmission (submit) exige le raccordement actif. `notice`
// non nul ⇔ org non raccordée : l'écran masque alors le formulaire et affiche ce
// texte (voir routes/invoices.ts, GET /b2b/ereporting).
export type B2cCategory = {
  ht: number;
  tva: number;
  rate: number;
  count: number;
  cash: number | null;
  card: number | null;
};

export type B2cEntry = {
  date: string; // 'YYYY-MM-DD'
  status: 'draft' | 'submitted';
  goods: B2cCategory | null;
  services: B2cCategory | null;
  totalTtc: number; // euros
  submittedAt: string | null;
  lastError: string | null;
};

// Payload d'entrée POST (euros, cash/card optionnels, cf. services/ereporting.ts).
export type B2cCategoryInput = {
  ht: number;
  tva: number;
  rate: number;
  count: number;
  cash?: number | null;
  card?: number | null;
};

export type B2cSaveInput = {
  date: string;
  goods?: B2cCategoryInput | null;
  services?: B2cCategoryInput | null;
};

export type EreportingData = {
  entries: B2cEntry[];
  notice: string | null;
  source: Source;
  reload: () => void;
};

export function useEreporting(): EreportingData {
  const [entries, setEntries] = useState<B2cEntry[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [source, setSource] = useState<Source>('loading');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setSource('loading');
    apiGet<{ entries?: B2cEntry[]; notice?: string | null }>('/api/b2b/ereporting', ctrl.signal)
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setEntries(d.entries ?? []);
        setNotice(d.notice ?? null);
        setSource('live');
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setEntries([]);
        setNotice(null);
        setSource('error');
        console.warn('[factumis] live fetch /api/b2b/ereporting failed', err);
      });
    return () => ctrl.abort();
  }, [tick]);

  return { entries, notice, source, reload: () => setTick((t) => t + 1) };
}

/** Pose ou met à jour le brouillon du jour (upsert par date). L'écran recharge
 *  la liste (reload()) après succès. */
export function saveB2cEntry(payload: B2cSaveInput): Promise<B2cEntry> {
  return apiPost<{ entry: B2cEntry }>('/api/b2b/ereporting', payload).then((d) => d.entry);
}

/** Supprime le brouillon du jour (draft uniquement, 409 ALREADY_SUBMITTED sinon). */
export function deleteB2cEntry(date: string): Promise<void> {
  return apiDelete<{ ok: boolean }>(`/api/b2b/ereporting/${date}`).then(() => undefined);
}

/** Transmet la journée à la plateforme agréée : action légale irréversible,
 *  l'appelant doit confirmer AVANT d'invoquer ce helper. */
export function submitB2cEntry(date: string): Promise<B2cEntry> {
  return apiPost<{ entry: B2cEntry }>(`/api/b2b/ereporting/${date}/submit`, {}).then((d) => d.entry);
}

// Achats étrangers (ledger foreign_purchases, Task 7/10) : déclaration des achats
// auprès de fournisseurs hors France, puis dépôt e-reporting « exempt incoming »
// à la plateforme agréée. Mêmes principes que le B2C ci-dessus (saisie sans gate
// PA, transmission avec gates complets et jeton d'usage), mais une ligne par achat
// (pas d'upsert par jour, un fournisseur peut être facturé plusieurs fois le même
// jour), voir routes/invoices.ts, POST/DELETE/GET/:id/submit /b2b/foreign-purchases.
export type ForeignPurchase = {
  id: number;
  date: string; // 'YYYY-MM-DD'
  supplierName: string;
  supplierVatId: string | null; // n° TVA intracom (UE) ou identifiant fiscal local, requis pour transmettre
  countryCode: string; // ISO 3166-1 alpha-2, jamais 'FR'
  status: 'draft' | 'submitted';
  ht: number; // euros
  vat: number; // euros, TVA autoliquidée saisie localement (jamais transmise à la PA)
  currency: string;
  paInvoiceId: string | null;
  submittedAt: string | null;
  lastError: string | null;
};

// Payload d'entrée POST : ht/vat transmis en chaîne (cf. z.string() du serveur),
// la conversion depuis les nombres saisis à l'écran reste interne à ce module.
export type ForeignPurchaseInput = {
  purchaseDate: string;
  supplierName: string;
  supplierVatId?: string;
  countryCode: string;
  ht: number;
  vat: number;
  currency?: string;
};

export function useForeignPurchases(): Resource<ForeignPurchase> {
  return useResource<ForeignPurchase>('/api/b2b/foreign-purchases', (j) => (j as { purchases?: ForeignPurchase[] }).purchases ?? []);
}

/** Pose un nouvel achat au statut 'draft'. L'écran recharge la liste (reload())
 *  après succès. */
export function saveForeignPurchase(input: ForeignPurchaseInput): Promise<void> {
  const payload = {
    purchaseDate: input.purchaseDate,
    supplierName: input.supplierName,
    ...(input.supplierVatId ? { supplierVatId: input.supplierVatId } : {}),
    countryCode: input.countryCode,
    ht: String(input.ht),
    vat: String(input.vat),
    ...(input.currency ? { currency: input.currency } : {}),
  };
  return apiPost<{ purchase: ForeignPurchase }>('/api/b2b/foreign-purchases', payload).then(() => undefined);
}

/** Supprime le brouillon (draft uniquement, 409 ALREADY_SUBMITTED sinon). */
export function deleteForeignPurchase(id: number): Promise<void> {
  return apiDelete<{ ok: boolean }>(`/api/b2b/foreign-purchases/${id}`).then(() => undefined);
}

/** Transmet l'achat à la plateforme agréée (dépôt exempt incoming) : action
 *  légale irréversible, l'appelant doit confirmer AVANT d'invoquer ce helper. */
export function submitForeignPurchase(id: number): Promise<void> {
  return apiPost<{ purchase: ForeignPurchase }>(`/api/b2b/foreign-purchases/${id}/submit`, {}).then(() => undefined);
}

// Notifications — dérivées côté serveur des factures émises/reçues, avec
// un état « lu » persisté. type/title/body/date sont prêts à l'affichage.
export type NotifType = 'overdue' | 'rejected' | 'paid' | 'received';
export type Notif = {
  key: string;
  type: NotifType;
  title: string;
  body: string;
  date: string;
  read: boolean;
};

export type NotificationsData = {
  rows: Notif[];
  unread: number;
  source: Source;
  reload: () => void;
  /** Marque une notification lue (optimiste + POST). */
  markRead: (key: string) => void;
  /** Marque tout comme lu (optimiste + POST). */
  markAllRead: () => void;
};

export function useNotifications(): NotificationsData {
  const [rows, setRows] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);
  const [source, setSource] = useState<Source>('loading');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setSource('loading');
    apiGet<{ notifications: Notif[]; unread: number }>('/api/b2b/notifications', ctrl.signal)
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setRows(d.notifications);
        setUnread(d.unread);
        setSource('live');
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setRows([]);
        setUnread(0);
        setSource('error');
        console.warn('[factumis] live fetch /api/b2b/notifications failed', err);
      });
    return () => ctrl.abort();
  }, [tick]);

  function markRead(key: string) {
    let changed = false;
    setRows((prev) =>
      prev.map((n) => {
        if (n.key === key && !n.read) { changed = true; return { ...n, read: true }; }
        return n;
      }),
    );
    if (changed) setUnread((u) => Math.max(0, u - 1));
    apiPost<{ unread: number }>('/api/b2b/notifications/read', { keys: [key] })
      .then((d) => setUnread(d.unread))
      .catch(() => {/* l'optimiste reste, resynchronisé au prochain chargement */});
  }

  function markAllRead() {
    setRows((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    apiPost<{ unread: number }>('/api/b2b/notifications/read-all', {})
      .then((d) => setUnread(d.unread))
      .catch(() => {/* idem */});
  }

  return { rows, unread, source, reload: () => setTick((t) => t + 1), markRead, markAllRead };
}

// ── Devis (module app, hors B2Brouter) ───────────────────────────────────────
export type QuoteTotals = { htCents: number; vatCents: number; ttcCents: number };
export type QuoteListItem = {
  id: number;
  number: string;
  clientName: string;
  status: string;
  issueDate: string;
  validUntil: string | null;
  /** Lien historique B2Brouter (devis convertis avant la bascule Seqino) : lien mort, sert juste à marquer « déjà converti ». */
  invoiceB2bId: number | null;
  /** Facture PA (Seqino) issue de la conversion (invoiceId technique, navigable). */
  invoicePaId: string | null;
  totals: QuoteTotals;
};

export function useQuotes(): Resource<QuoteListItem> {
  return useResource<QuoteListItem>('/api/quotes', (j) => (j as { quotes?: QuoteListItem[] }).quotes ?? []);
}

// Dashboard aggregates + recent invoices.
export type MonthlyCaPoint = { month: string; emis: number; encaisse: number };
export type DashboardData = {
  encaisse: number;
  enAttente: number;
  enRetard: number;
  recent: Invoice[];
  /** Série mensuelle du graphique CA (mois continus jusqu'au mois courant), euros. */
  monthly: MonthlyCaPoint[];
  source: Source;
  reload: () => void;
};

const EMPTY_DASHBOARD = { encaisse: 0, enAttente: 0, enRetard: 0, recent: [], monthly: [] as MonthlyCaPoint[] };

export function useDashboard(): DashboardData {
  const [data, setData] = useState<Omit<DashboardData, 'source' | 'reload'>>(EMPTY_DASHBOARD);
  const [source, setSource] = useState<Source>('loading');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setSource('loading');
    apiGet<{ encaisse: number; enAttente: number; enRetard: number; recent: Invoice[]; monthly?: MonthlyCaPoint[] }>(
      '/api/b2b/dashboard',
      ctrl.signal,
    )
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setData({ ...d, monthly: d.monthly ?? [] });
        setSource('live');
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setData(EMPTY_DASHBOARD);
        setSource('error');
        console.warn('[factumis] live fetch /api/b2b/dashboard failed', err);
      });
    return () => ctrl.abort();
  }, [tick]);

  return { ...data, source, reload: () => setTick((t) => t + 1) };
}

// ── Catalogue produits/services ──────────────────────────────────────────────
export type CatalogItem = { id: number; label: string; unitPriceCents: number; vatRate: number };
export type CatalogInput = { label: string; unitPriceCents: number; vatRate: number };

export function useCatalog(): Resource<CatalogItem> {
  return useResource<CatalogItem>('/api/catalog', (j) => (j as { items?: CatalogItem[] }).items ?? []);
}

export function createCatalogItem(input: CatalogInput): Promise<CatalogItem> {
  return apiPost<{ item: CatalogItem }>('/api/catalog', input).then((d) => d.item);
}

export function updateCatalogItem(id: number, input: Partial<CatalogInput>): Promise<CatalogItem> {
  return apiPatch<{ item: CatalogItem }>(`/api/catalog/${id}`, input).then((d) => d.item);
}

export function deleteCatalogItem(id: number): Promise<void> {
  return apiDelete<{ ok: boolean }>(`/api/catalog/${id}`).then(() => undefined);
}

// ── Modèles de facture/devis (pré-remplissage) ───────────────────────────────
export type TemplateLine = { label: string; quantity: number; unitPriceCents: number; vatRate: number };
export type InvoiceTemplate = {
  id: number;
  name: string;
  defaultNotes: string | null;
  defaultDueDays: number | null;
  lines: TemplateLine[];
};
export type TemplateInput = Omit<InvoiceTemplate, 'id'>;

export function useTemplates(): Resource<InvoiceTemplate> {
  return useResource<InvoiceTemplate>('/api/templates', (j) => (j as { templates?: InvoiceTemplate[] }).templates ?? []);
}
export function createTemplate(input: TemplateInput): Promise<InvoiceTemplate> {
  return apiPost<{ template: InvoiceTemplate }>('/api/templates', input).then((d) => d.template);
}
export function updateTemplate(id: number, input: Partial<TemplateInput>): Promise<InvoiceTemplate> {
  return apiPatch<{ template: InvoiceTemplate }>(`/api/templates/${id}`, input).then((d) => d.template);
}
export function deleteTemplate(id: number): Promise<void> {
  return apiDelete<{ ok: boolean }>(`/api/templates/${id}`).then(() => undefined);
}
